"use client";

import { cn } from "@ui/lib";
import { getToolName, isTextUIPart, isToolUIPart, type UIMessage } from "ai";
import { PauseIcon, PencilIcon, PlayIcon } from "lucide-react";
import { memo, useEffect, useRef, useState } from "react";
import { ProductResults, type SetQuantity } from "./ProductCardView";
import { RecipeCard } from "./RecipeCard";
import { RetailerMark } from "./RetailerMark";
import { ShopperMarkdown } from "./ShopperMarkdown";
import { ToolStep } from "./ToolStep";
import {
	type BasketPick,
	type ProductCard,
	productRowsFrom,
	type RecipeInput,
	type RecipeOutput,
} from "./types";
import { formatDuration, resampleBars, type VoiceNote } from "./voice/hooks";

export interface ShopperMessageMetadata {
	voiceNote?: VoiceNote;
	/** Written by a person on the retailer's team (handoff), not the assistant. */
	staff?: { name: string | null };
	/** A turn nobody answered because a person holds the conversation: nothing to show. */
	agentPaused?: boolean;
}

export interface MessageContext {
	/** Who is answering: shown above each reply. */
	assistant: AssistantIdentity;
	quantities: Map<string, number>;
	onQuantity: SetQuantity;
	onAddAll: (picks: BasketPick[]) => Promise<boolean>;
	onFixTranscript: (text: string) => void;
	onOpenProduct: (product: ProductCard) => void;
	/** The chat is on screen: a voice note stops playing when it is not. */
	shown: boolean;
	/** What the assistant is doing, above its results (`chat.showSteps`). */
	showSteps: boolean;
	/** The + on product cards (`chat.addFromCards`). */
	addable: boolean;
	/** What the status beside the name reads while a reply is made (`chat.labels`). */
	status: { thinking: string; typing: string };
}

export function messageText(message: UIMessage): string {
	return message.parts
		.filter(isTextUIPart)
		.map((part) => part.text)
		.join("\n")
		.trim();
}

/**
 * One message. Memoized: the AI SDK keeps earlier messages the same object
 * between updates, so while a reply streams only the last one re-renders.
 */
export const ShopperMessage = memo(function ShopperMessage({
	message,
	complete,
	arabic,
	context,
}: {
	message: UIMessage;
	/** False while this reply is still streaming. */
	complete: boolean;
	/** The shopper's last message was in Arabic script. */
	arabic: boolean;
	context: MessageContext;
}) {
	if (message.role === "user") {
		const voiceNote = (
			message.metadata as ShopperMessageMetadata | undefined
		)?.voiceNote;
		const text = messageText(message);
		return (
			<div className="flex animate-shop-rise justify-end">
				{voiceNote ? (
					<VoiceNoteBubble
						note={voiceNote}
						shown={context.shown}
						onFix={() => context.onFixTranscript(text)}
					/>
				) : (
					<p
						dir="auto"
						className="max-w-[82%] whitespace-pre-wrap rounded-[calc(var(--radius)*2.75)] rounded-ee-md bg-(--shop-bubble) px-4 py-2.5 text-(--shop-on-bubble) text-[15px] leading-snug"
					>
						{text}
					</p>
				)}
			</div>
		);
	}

	const metadata = message.metadata as ShopperMessageMetadata | undefined;
	if (metadata?.agentPaused) {
		return null;
	}
	if (metadata?.staff) {
		return (
			<AssistantTurn
				identity={{
					...context.assistant,
					name: metadata.staff.name ?? context.assistant.retailerName,
				}}
				team
				status={null}
			>
				<p
					dir="auto"
					className="whitespace-pre-wrap text-(--shop-ink) text-[15.5px] leading-relaxed"
				>
					{messageText(message)}
				</p>
			</AssistantTurn>
		);
	}

	const parts = visibleParts(message, complete);
	if (parts.length === 0 && complete) {
		return null;
	}
	const hasText = parts.some(({ part }) => isTextUIPart(part));

	return (
		<AssistantTurn
			identity={context.assistant}
			status={
				complete
					? null
					: hasText
						? context.status.typing
						: context.status.thinking
			}
		>
			{/* Where the answer will be, until its first words arrive. */}
			{!complete && !hasText ? <PendingText /> : null}
			{parts.map(({ part, index }) => {
				const key = `${message.id}-${index}`;
				if (isTextUIPart(part)) {
					return (
						<ShopperMarkdown
							key={key}
							streaming={!complete && part.state === "streaming"}
							className="text-(--shop-ink) text-[15.5px] leading-relaxed [&_[data-streamdown=link]]:text-(--shop-brand-text) [&_li]:marker:text-(--shop-brand-text)"
						>
							{part.text}
						</ShopperMarkdown>
					);
				}
				if (!isToolUIPart(part)) {
					return null;
				}
				const toolName = getToolName(part);
				const done = part.state === "output-available";
				const recipe = toolName === "find_ingredients";
				const rows =
					done && !recipe ? productRowsFrom(part.output) : [];
				return (
					<div key={key} className="flex min-w-0 flex-col gap-2.5">
						{/* A step that failed is always said: the reply may lean on it. */}
						{context.showSteps || part.state === "output-error" ? (
							<ToolStep
								toolName={toolName}
								input={part.input}
								output={done ? part.output : undefined}
								done={done}
								failed={part.state === "output-error"}
							/>
						) : null}
						{recipe && part.state !== "output-error" ? (
							<RecipeCard
								input={part.input as RecipeInput | undefined}
								output={
									done
										? (part.output as RecipeOutput)
										: undefined
								}
								streaming={part.state === "input-streaming"}
								quantities={context.quantities}
								onQuantity={context.onQuantity}
								onAddAll={context.onAddAll}
								onOpen={context.onOpenProduct}
							/>
						) : null}
						{rows.map((products) => (
							<ProductResults
								key={products[0]?.id}
								products={products}
								arabic={arabic}
								quantities={context.quantities}
								onQuantity={context.onQuantity}
								onOpen={context.onOpenProduct}
								addable={context.addable}
							/>
						))}
					</div>
				);
			})}
		</AssistantTurn>
	);
});

export interface AssistantIdentity {
	name: string;
	retailerName: string;
	logo: string | null;
	/** The retailer's mark beside the name (`chat.showLogo`). */
	showLogo: boolean;
	/** The name itself (`chat.showAssistantName`). A person's name is always shown. */
	showName: boolean;
}

/**
 * One reply: the retailer's mark and the assistant's name on a slim row
 * (with what it is doing while it works), then the answer at full width.
 */
export function AssistantTurn({
	identity,
	status,
	team = false,
	still = false,
	children,
}: {
	identity: AssistantIdentity;
	/** "Thinking" or "Typing" (or the retailer's words) while the reply is being made; null once done. */
	status: string | null;
	/** A person from the retailer's team wrote it. */
	team?: boolean;
	/**
	 * No entrance: the skeleton in the server's HTML. The entrance starts at opacity 0, and a
	 * first paint that shows nothing is not one.
	 */
	still?: boolean;
	children: React.ReactNode;
}) {
	const named = identity.showName || team;
	return (
		<div
			className={cn(
				"flex min-w-0 flex-col gap-2.5",
				!still && "animate-shop-rise",
			)}
		>
			{/* With the logo and the name both off, the row is there only while a reply is made. */}
			<div
				className={cn(
					"flex min-w-0 items-center gap-2",
					!identity.showLogo && !named && !status && "hidden",
				)}
			>
				{identity.showLogo ? (
					<RetailerMark
						name={identity.retailerName}
						logo={identity.logo}
						className="size-6 text-[9px]"
					/>
				) : null}
				{named ? (
					<span className="truncate font-semibold text-(--shop-ink) text-[13px]">
						{identity.name}
					</span>
				) : null}
				{team ? (
					<span className="shrink-0 rounded-full bg-(--shop-accent-soft) px-2 py-0.5 font-semibold text-(--shop-accent-ink) text-[11px]">
						Team
					</span>
				) : null}
				{status ? (
					<span
						role="status"
						className="shimmer shrink-0 text-(--shop-muted) text-[13px]"
					>
						{status}…
					</span>
				) : null}
			</div>
			<div className="flex min-w-0 flex-col gap-3">{children}</div>
		</div>
	);
}

/** Placeholder lines in the answer's place while the model is still thinking. */
export function PendingText() {
	return (
		<div aria-hidden className="flex flex-col gap-2.5 py-1.5">
			{["w-[88%]", "w-[64%]"].map((width, i) => (
				<span
					key={width}
					style={{ animationDelay: `${i * 150}ms` }}
					className={cn(
						"h-3 animate-pulse rounded-full bg-(--shop-brand-line)",
						width,
					)}
				/>
			))}
		</div>
	);
}

/** What the shopper said by voice: playable, with what we heard underneath. */
function VoiceNoteBubble({
	note,
	shown,
	onFix,
}: {
	note: VoiceNote;
	shown: boolean;
	onFix: () => void;
}) {
	const audio = useRef<HTMLAudioElement | null>(null);
	const [playing, setPlaying] = useState(false);
	const [progress, setProgress] = useState(0);
	const bars = resampleBars(note.bars, 30);

	useEffect(() => {
		if (!note.audioUrl) {
			return;
		}
		const element = new Audio(note.audioUrl);
		audio.current = element;
		element.ontimeupdate = () =>
			setProgress(
				element.currentTime / Math.max(0.1, note.durationMs / 1000),
			);
		// However it stopped (the button, a hide, the system taking the audio).
		element.onpause = () => setPlaying(false);
		element.onended = () => {
			setPlaying(false);
			setProgress(0);
		};
		return () => {
			element.pause();
			audio.current = null;
		};
	}, [note.audioUrl, note.durationMs]);

	// The host closed the chat: nothing keeps playing in a web view nobody sees.
	useEffect(() => {
		if (!shown) {
			audio.current?.pause();
		}
	}, [shown]);
	// Off screen it is paused, before the element's `pause` event says so.
	const active = playing && shown;

	const toggle = () => {
		const element = audio.current;
		if (!element) {
			return;
		}
		if (active) {
			element.pause();
			setPlaying(false);
		} else {
			element.play().then(
				() => setPlaying(true),
				() => setPlaying(false),
			);
		}
	};

	return (
		<div className="flex w-[82%] max-w-80 flex-col gap-2 rounded-[calc(var(--radius)*2.75)] rounded-ee-md bg-(--shop-bubble) p-2.5 ps-2.5 text-(--shop-on-bubble)">
			<div className="flex items-center gap-2.5">
				<button
					type="button"
					onClick={toggle}
					disabled={!note.audioUrl}
					aria-label={active ? "Pause voice note" : "Play voice note"}
					className="flex size-10 shrink-0 items-center justify-center rounded-full bg-(--shop-on-bubble) text-(--shop-bubble) outline-none focus-visible:ring-3 focus-visible:ring-(--shop-on-bubble)/50 disabled:opacity-60"
				>
					{active ? (
						<PauseIcon className="size-4 fill-current" />
					) : (
						<PlayIcon className="ms-0.5 size-4 fill-current" />
					)}
				</button>
				<span
					aria-hidden
					className="flex h-7 flex-1 items-center gap-[2px]"
				>
					{bars.map((bar, i) => (
						<span
							key={i}
							style={{ height: `${Math.round(bar * 100)}%` }}
							className={cn(
								"w-[3px] min-h-1 rounded-full",
								i / bars.length < progress
									? "bg-(--shop-accent)"
									: "bg-(--shop-on-bubble)/55",
							)}
						/>
					))}
				</span>
				<span className="shrink-0 font-medium text-xs tabular opacity-85">
					{formatDuration(note.durationMs)}
				</span>
			</div>
			<div className="flex items-end gap-2 rounded-2xl bg-(--shop-on-bubble)/12 px-3 py-2">
				<p
					dir="auto"
					className="min-w-0 flex-1 text-[14px] leading-snug"
				>
					{note.transcript}
				</p>
				<button
					type="button"
					onClick={onFix}
					className="flex shrink-0 items-center gap-1 rounded-full bg-(--shop-on-bubble)/18 px-2.5 py-1 font-semibold text-[11px] outline-none hover:bg-(--shop-on-bubble)/28 focus-visible:ring-2 focus-visible:ring-white/60"
				>
					<PencilIcon className="size-3" aria-hidden />
					Fix
				</button>
			</div>
		</div>
	);
}

/**
 * The parts of an assistant message that show anything, answer first: the
 * text reads before the products it refers to. Empty for a turn that died
 * before it said or found anything, so the chat can offer a retry instead
 * of a blank row.
 */
export function visibleParts(message: UIMessage, complete: boolean) {
	const shown = message.parts
		.map((part, index) => ({ part, index }))
		.filter(({ part }) => {
			if (isTextUIPart(part)) {
				return part.text.trim().length > 0;
			}
			if (!isToolUIPart(part)) {
				return false;
			}
			// A reply cut off mid-call (the tab closed, the turn was
			// stopped) is saved with its call unfinished: nothing ran.
			if (
				complete &&
				(part.state === "input-streaming" ||
					part.state === "input-available")
			) {
				return false;
			}
			// Arguments that didn't parse, then a retry: an internal hiccup,
			// not something the shopper did or needs to see.
			return !(part.state === "output-error" && part.input == null);
		});
	return [
		...shown.filter(({ part }) => isTextUIPart(part)),
		...shown.filter(({ part }) => !isTextUIPart(part)),
	];
}
